import { Col, DatePicker, Dropdown, Form, Input, Radio, Row, Select as AntdSelect, Space, Switch, Typography } from 'antd';
import moment from 'moment';
import * as React from 'react';
import { useRecoilState } from 'recoil';
import { BankEditState, EditBankResult, EditForm } from 'src/hooks/useBankEdit';
import { useDepositBankAccounts } from 'src/hooks/useDepositBankAccount';
import { mapToBankTypeText } from 'src/mapper/mapToBank';
import { modalState } from 'src/recoils';
import { BankRow, BankAccountStatus, BankCode, BANK_NAME, BANK_TYPE } from 'src/types';
import { getBankFormat, getPhoneFormat } from 'src/utils/textOperation/textOperation';
import { BankLogo } from '..';
import Status from '../Bank';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import { Menu, MenuItem } from '../Menu/Menu';
import Modal from '../Modal/Modal';
import colors from 'src/theme/colors.json';
import './EditBankForm.less';
import { useRecoilValue } from 'recoil';
import { authInfoState } from 'src/recoils';
import { Role } from 'src/types';

const GROUP_BANK_SELECTABLE_BANKS: BankCode[] = [BankCode.SCB, BankCode.KBANK];


export type IEditBankFormProps = {
  editBankState: BankEditState;
  editingBank?: BankRow;
  onCancel: () => void;
  onEditConfirm: (form: EditForm, successText?: string, errorText?: string) => void;
};


const EditBankForm: React.FunctionComponent<IEditBankFormProps> = (props) => {
  const [, setModal] = useRecoilState(modalState);
  const { editBankState, editingBank, onCancel, onEditConfirm } = props;
  const { editBankResult, setEditBankResult } = editBankState;
  const [form] = Form.useForm<EditForm>()
  const [disableEditButton, setDisableEditButton] = React.useState<boolean>(true);
  const [disableForcePending, setDisableForcePending] = React.useState<boolean>(true);
  const { role } = useRecoilValue(authInfoState)

  const shouldShowGroupBankSelect = !!editingBank
    && editingBank.type === BANK_TYPE.DEPOSIT
    && GROUP_BANK_SELECTABLE_BANKS.some((b) => b === editingBank.bankCode);

  const { data: depositBankAccounts } = useDepositBankAccounts(
    undefined,
    undefined,
    shouldShowGroupBankSelect,
  );

  const bankThaiName = editingBank
    ? BANK_NAME[editingBank.bankCode as keyof typeof BANK_NAME]
    : undefined;
  const matchesThaiName = React.useCallback(
    (name?: string) => !bankThaiName || (name?.includes(bankThaiName) ?? false),
    [bankThaiName],
  );

  // If the current group doesn't match the bank's Thai name (e.g. KBANK
  // account currently sits in a "HYRONPAY" group) we don't allow editing —
  // the dropdown still shows the current name but is disabled.
  const isCurrentGroupBankEditable = !editingBank?.groupBankName
    || matchesThaiName(editingBank?.groupBankName);

  const groupBankOptions = React.useMemo(() => {
    if (!editingBank) {
      return [];
    }
    const seenGroupBankUids = new Set<string>();
    const options: { label: string; value: string }[] = [];
    // Pre-seed with the current selection so its label renders correctly
    // before the API response is available — even if it doesn't match the
    // Thai-name filter (in that case the select is disabled below).
    if (editingBank.groupBankId && editingBank.groupBankName) {
      options.push({
        label: editingBank.groupBankName,
        value: editingBank.groupBankId,
      });
      seenGroupBankUids.add(editingBank.groupBankId);
    }
    if (!depositBankAccounts) {
      return options;
    }
    const apiOptions = depositBankAccounts
      .filter((account) => account.groupBankBankCode === editingBank.bankCode)
      .filter((account) => matchesThaiName(account.groupBankName))
      .filter((account) => {
        if (!account.groupBankUid || seenGroupBankUids.has(account.groupBankUid)) {
          return false;
        }
        seenGroupBankUids.add(account.groupBankUid);
        return true;
      })
      .map((account) => ({
        label: account.groupBankName,
        value: account.groupBankUid,
      }));
    return [...options, ...apiOptions];
  }, [depositBankAccounts, editingBank, matchesThaiName]);

  React.useEffect(() => {
    if (editingBank) {
      form.setFieldsValue({
        type: editingBank.type,
        status: editingBank.status,
        promptpay: editingBank.promptpay,
        trueKey: editingBank.trueKey,
        isAuto: editingBank.isAuto,
        isWebAuto: editingBank.isWebAuto,
        forcePending: editingBank.forcePending,
        isApi: editingBank.isApi,
        isSms: editingBank.isSms,
        cooldownMinute: editingBank.cooldownMinute,
        cooldownNumber: editingBank.cooldownNumber,
        dailyWithdrawalLimit: editingBank.dailyWithdrawalLimit,
        priorityOrder: editingBank.priorityOrder,
        groupBankName: editingBank.groupBankId,
      });
      setDisableEditButton(true)
      setDisableForcePending(!editingBank.isAuto);
    } else {
      form.resetFields()
      setEditBankResult({})
    }
  }, [editingBank]);

  const checkDisableEditButton = (editForm: EditForm) => {
    if (!editingBank) return;
    const baseCheckingField =
      editForm.status === editingBank.status &&
      editForm.isAuto === editingBank.isAuto &&
      editForm.cooldownMinute === editingBank.cooldownMinute &&
      editForm.cooldownNumber === editingBank.cooldownNumber &&
      editForm.dailyWithdrawalLimit === editingBank.dailyWithdrawalLimit &&
      editForm.priorityOrder === editingBank.priorityOrder &&
      moment(editForm.forcePending).isSame(moment(editingBank.forcePending));
    const isPriorityOrderValid = editForm.priorityOrder && Number(editForm.priorityOrder) > 0;
    const dailyWithdrawalLimitValid =
    (editForm.dailyWithdrawalLimit === null || editForm.dailyWithdrawalLimit === undefined ) ||
      (typeof editForm.dailyWithdrawalLimit === "string" && editForm.dailyWithdrawalLimit === "")
    || Number(editForm.dailyWithdrawalLimit) > 0;
    if (editingBank?.type === BANK_TYPE.DEPOSIT) {
      const depositFormDoesNotChange = baseCheckingField &&
        editForm.promptpay === editingBank.promptpay &&
        editForm.isApi === editingBank.isApi &&
        editForm.isSms === editingBank.isSms &&
        (editForm.groupBankName ?? undefined) === (editingBank.groupBankId ?? undefined)
      const groupBankInvalid = shouldShowGroupBankSelect && !editForm.groupBankName;
      setDisableEditButton(depositFormDoesNotChange || groupBankInvalid)
    }

    if (editingBank?.type === BANK_TYPE.WITHDRAW) {
      const withdrawFormDoesNotChange = baseCheckingField && editForm.isWebAuto === editingBank.isWebAuto;
      setDisableEditButton(withdrawFormDoesNotChange || !(isPriorityOrderValid && dailyWithdrawalLimitValid));
    }
  }

  const onEdit = () => {
    const editingForm = form.getFieldsValue();
    if (editingBank) {
      const isStatusChanged = editingForm.status !== editingBank.status;
      let isAuto = editingForm.isAuto;
      let isWebAuto = editingForm.isWebAuto;
      // if (isStatusChanged) {
      //   if (editingForm.status === 'Active') {
      //     isAuto = false;
      //     isWebAuto = false;
      //   }
      //   if (editingForm.status === 'Closed') {
      //     isAuto = false;
      //     isWebAuto = false;
      //   }
      // }
      setModal({
        isModalVisible: true,
        type: 'warning',
        title: 'แก้ไขบัญชี',
        content: 'ต้องการแก้ไขบัญชีนี้ใช่หรือไม่',
        buttonType: 'question',
        onConfirm: () => {
          onEditConfirm({
            ...editingForm,
            bankId: editingBank.bankId,
            type: editingBank.type,
            isAuto: isAuto,
            isWebAuto: isWebAuto
          }, 'แก้ไขสำเร็จ', 'แก้ไขไม่สำเร็จ')
        },
      });
    }
  }

  const onInactiveBank = (status: BankAccountStatus) => {
    const editingForm = form.getFieldsValue();
    if (editingBank) {
      setModal({
        isModalVisible: true,
        type: 'warning',
        title: 'ปิดบัญชี',
        content: 'ต้องการปิดบัญชีนี้ใช่หรือไม่',
        buttonType: 'question',
        onConfirm: () => {
          onEditConfirm({
            bankId: editingBank.bankId,
            promptpay: editingForm.promptpay,
            trueKey: editingForm.trueKey,
            type: editingBank.type,
            isAuto: false,
            isWebAuto: false,
            status: status,
          }, 'ปิดบัญชีสำเร็จ', 'ปิดบัญชีไม่สำเร็จ')
        },
      });
    }
  }

  const formatAccount = editingBank?.bankCode === BankCode.TrueMoney ? getPhoneFormat : getBankFormat

  return (
    <Modal
      centered
      className="EditBankForm"
      visible={editingBank !== undefined}
      title="แก้ไขข้อมูลบัญชี"
      footer={editingBank && (
        <>
          <Button size='large' style={{ width: 100 }} onClick={() => { onCancel() }}>
            ยกเลิก
          </Button>
          <Button
            disabled={disableEditButton}
            htmlType='submit'
            size='large'
            type='primary'
            style={{ width: 180 }}
            onClick={() => { onEdit() }}
          >
            บันทึก
          </Button>
        </>
      )}
    >
      {editingBank && (
        <>
          <div className='EditBankForm_Heading'>
            <div><Space><Typography.Text strong>ประเภท:</Typography.Text><Typography>{mapToBankTypeText(editingBank.type)}</Typography></Space></div>
            <div><Space><Typography.Text strong>ธนาคาร:</Typography.Text><BankLogo bankCode={editingBank.bankCode} />{editingBank.bankName}</Space></div>
            <div><Space><Typography.Text strong>ชื่อบัญชี:</Typography.Text><Typography>{editingBank.accountName}</Typography></Space></div>
            <div><Space><Typography.Text strong>เลขที่บัญชี:</Typography.Text><Typography>{formatAccount(editingBank.accountNumber)}</Typography></Space></div>
            {formatAccount(editingBank.trueKey) && <div><Space><Typography.Text strong>True KEY:</Typography.Text><Typography>{editingBank.trueKey}</Typography></Space></div>}
            <Dropdown
              arrow
              placement="bottomRight"
              overlay={(
                <Menu>
                  <MenuItem key="Banned" onClick={(e) => { onInactiveBank(e.keyPath[0] as BankAccountStatus) }}>
                    <Icon name='outline-notbank-2' size='22px' />&nbsp;ถูกธนาคารระงับใช้งาน
                  </MenuItem>
                  <MenuItem key="Terminated" onClick={(e) => { onInactiveBank(e.keyPath[0] as BankAccountStatus) }}>
                    <Icon name='outline-notother-2' size='22px' />&nbsp;สาเหตุอื่นๆ
                  </MenuItem>
                </Menu>
              )}>
              <Button shape="round" style={{ position: 'absolute', top: '18px', right: '24px' }}>ปิดบัญชี</Button>
            </Dropdown>
          </div>
          <div className='EditBankForm_BodyForm'>
            <Form
              form={form}
              name="editBankForm"
              layout="vertical"
              onValuesChange={(changedValues, _form) => {
                const isSwitchingAuto = changedValues.isAuto !== undefined;
                if (isSwitchingAuto) {
                  form.setFieldsValue({ forcePending: undefined });
                }
                checkDisableEditButton(_form);
                setDisableForcePending(!_form.isAuto);
              }}
            >
              <Row>
                <Col span={24}>
                  <Typography.Text strong className="text-cloud-7">ระบบอัตโนมัติ</Typography.Text>
                </Col>
                <Col span={12}>
                  <Space align="baseline">
                    <Form.Item label="" valuePropName="checked" name="isAuto">
                      <Switch checkedChildren="เปิด" unCheckedChildren="ปิด" />
                    </Form.Item>
                    ระบบหลังบ้าน
                  </Space>
                </Col>
                <Col span={12}>
                  {editingBank.type === BANK_TYPE.WITHDRAW && (
                    <Space align="baseline">
                      <Form.Item label="" valuePropName="checked" name="isWebAuto">
                        <Switch checkedChildren="เปิด" unCheckedChildren="ปิด" />
                      </Form.Item>
                      หน้าเว็บไซต์
                    </Space>
                  )}
                </Col>
                {(editingBank.type === BANK_TYPE.WITHDRAW) && (role === Role.OWNER || role === Role.DM) && (
                  <>
                    <Col span={12}>
                      <Space align="baseline">
                          <Form.Item label="จำนวนรายการ cool down" name="cooldownNumber"
                            validateStatus={editBankResult?.cooldown_number?.validateStatus}
                            help={editBankResult?.cooldown_number?.help}
                            rules={[
                              { pattern: new RegExp(/^[1-9]\d*$/), message: 'รูปแบบไม่ถูกต้อง' },
                            ]}
                          >
                            <Input placeholder="ใส่จำนวนรายการ cool down" size='middle' />
                          </Form.Item>
                      </Space>
                    </Col>
                    <Col span={12}>
                        <Space align="baseline">
                          <Form.Item label="เวลาในการ cool down (นาที)" name="cooldownMinute"
                            validateStatus={editBankResult?.cooldown_minute?.validateStatus}
                            help={editBankResult?.cooldown_minute?.help}
                            rules={[
                              { pattern: new RegExp(/^[1-9]\d*$/), message: 'รูปแบบไม่ถูกต้อง' },
                            ]}
                          >
                            <Input placeholder="ใส่เวลาในการ cool down (นาที)" size='middle' />
                          </Form.Item>
                        </Space>
                    </Col>
                    <Col span={12}>
                      <Space align="baseline">
                        <Form.Item label="ลำดับ" name="priorityOrder"
                                   validateStatus={editBankResult?.priority_order?.validateStatus}
                            help={editBankResult?.priority_order?.help}
                            rules={[
                              { required: true, message: 'กรุณากรอกลำดับ' },
                              { pattern: new RegExp(/^[0-9]\d*$/), message: 'รูปแบบไม่ถูกต้อง' },
                            ]}
                          >
                          <Input placeholder="ลำดับ" size='middle' />
                        </Form.Item>
                      </Space>
                  </Col>
                  <Col span={12}>
                      <Space align="baseline">
                        <Form.Item label="ลิมิตการถอนต่อวัน" name="dailyWithdrawalLimit"
                                   validateStatus={editBankResult?.daily_withdrawal_limit?.validateStatus}
                            help={editBankResult?.daily_withdrawal_limit?.help}
                            rules={[
                              { pattern: new RegExp(/^[1-9]\d*$/), message: 'รูปแบบไม่ถูกต้อง' },
                            ]}
                          >
                          <Input placeholder="ใส่ลิมิตการถอนต่อวัน" size='middle' />
                        </Form.Item>
                      </Space>
                  </Col>
                  </>
                )}
                <Col span={24}>
                  <Form.Item label="ตั้งเวลาปิด" name="forcePending">
                    <DatePicker showTime size='large' disabled={disableForcePending} />
                  </Form.Item>
                </Col>
                {editingBank.type === BANK_TYPE.DEPOSIT && editingBank.bankCode !== BankCode.TrueMoney && (
                  <>
                    <Col span={24}>
                      <Typography.Text strong className="text-cloud-7">ระบบ scraping</Typography.Text>
                    </Col>
                    <Col span={12}>
                      <Space align="baseline">
                        <Form.Item label="" valuePropName="checked" name="isSms">
                          <Switch checkedChildren="เปิด" unCheckedChildren="ปิด" />
                        </Form.Item>
                        SMS
                      </Space>
                    </Col>
                    <Col span={12}>
                      <Space align="baseline">
                        <Form.Item label="" valuePropName="checked" name="isApi">
                          <Switch checkedChildren="เปิด" unCheckedChildren="ปิด" />
                        </Form.Item>
                        API
                      </Space>
                    </Col>
                  </>
                )}
                {!(editingBank.bankCode === BankCode.TrueMoney || editingBank.type === BANK_TYPE.WITHDRAW) && (
                  <Col span={24}>
                    <Form.Item label="เบอร์พร้อมเพย์" name="promptpay"
                      validateStatus={editBankResult?.promptpay?.validateStatus}
                      help={editBankResult?.promptpay?.help}
                      rules={[
                        { pattern: new RegExp(/(0){1}(\d{9})/), message: 'รูปแบบไม่ถูกต้อง' },
                      ]}
                    >
                      <Input placeholder="ใส่เบอร์พร้อมเพย์" size='large' maxLength={10} />
                    </Form.Item>
                  </Col>
                )}
                {(editingBank.bankCode === BankCode.TrueMoney) && (
                  <Col span={24}>
                    <Form.Item
                      label="key สำหรับ TRUE อัตโนมัติ"
                      name="trueKey"
                    >
                      <Input placeholder='ใส่ key สำหรับ TRUE อัตโนมัติ (ถ้ามี)' size='large' maxLength={255} />
                    </Form.Item>
                  </Col>
                )}
                {shouldShowGroupBankSelect && (
                  <Col span={24}>
                    <Form.Item
                      label="กลุ่มบัญชีธนาคาร"
                      name="groupBankName"
                      rules={[{ required: true, message: 'กรุณาเลือกกลุ่มบัญชีธนาคาร' }]}
                    >
                      <AntdSelect
                        size='large'
                        placeholder='เลือกกลุ่มบัญชีธนาคาร'
                        suffixIcon={(<Icon name='outline-arrow-down-1' color={colors['@cloud-6']} />)}
                        options={groupBankOptions}
                        disabled={!isCurrentGroupBankEditable}
                      />
                    </Form.Item>
                  </Col>
                )}
                <Col span={24}>
                  <Form.Item label="สถานะ" name="status">
                    <Radio.Group>
                      <Space direction="vertical">
                        <Radio value={'Active'}><Status type={'Active'} /></Radio>
                        <Radio value={'Closed'}><Status type={'Closed'} /></Radio>
                        <Radio value={'OverTransaction'} disabled><Status type={'OverTransaction'} /></Radio>
                      </Space>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
        </>
      )}
    </Modal>
  );
};

export default EditBankForm;
